import { useNavigate } from 'react-router-dom';
import { ClipboardList, AlertCircle, Award, Clock, Info } from 'lucide-react';
import { formatDistanceToNow, isPast, parseISO, differenceInDays } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { usePortalAssessments, usePortalCertifications, usePortalMe } from '@/api/hooks/usePortal';
import { cn } from '@/lib/utils';
import type { PortalAssessment } from '@/api/types/assessment.types';

/**
 * Time-of-day, name-personalized portal greeting (Sub-sprint A).
 * Clean no-name fallback: when there is no first name it reads "Good morning"
 * (no trailing comma, no "there"). Thresholds: <12 morning / <17 afternoon / else evening.
 */
export function portalGreeting(firstName: string | null | undefined, now: Date = new Date()): string {
  const hour = now.getHours();
  const base = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const name = firstName?.trim();
  return name ? `${base}, ${name}` : base;
}

function AssessmentCard({ assessment }: { assessment: PortalAssessment }) {
  const navigate = useNavigate();
  const overdue = assessment.due_date ? isPast(parseISO(assessment.due_date)) : false;
  const progress = assessment.total_questions > 0
    ? (assessment.answered_count / assessment.total_questions) * 100
    : 0;

  return (
    <Card className={cn('transition-shadow hover:shadow-md', overdue && 'border-accent-rose/30')}>
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <p className="text-xs text-text-secondary">{assessment.tenant_name}</p>
            <p className="font-semibold text-text-primary">{assessment.title}</p>
          </div>
          <StatusBadge status={assessment.status} />
        </div>

        {/* Due date */}
        {assessment.due_date && (
          <div className={cn('flex items-center gap-1.5 text-sm', overdue ? 'text-accent-rose' : 'text-text-secondary')}>
            {overdue ? <AlertCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
            <span>
              {overdue
                ? `Overdue ${formatDistanceToNow(parseISO(assessment.due_date), { addSuffix: true })}`
                : `Due ${formatDistanceToNow(parseISO(assessment.due_date), { addSuffix: true })}`}
            </span>
          </div>
        )}

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs text-text-secondary">
            <span>{assessment.answered_count} of {assessment.total_questions} questions answered</span>
            <span className="font-mono">{Math.round(progress)}%</span>
          </div>
          <div className="h-2 rounded-full bg-bg-surface-2">
            <div
              className={cn('h-full rounded-full transition-all', progress === 100 ? 'bg-accent-emerald' : 'bg-accent-cyan')}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <Button
          className="w-full"
          size="sm"
          onClick={() => navigate(`/portal/assessments/${assessment.id}`)}
        >
          {progress === 0 ? 'Start Assessment' : progress === 100 ? 'Review & Submit' : 'Continue Assessment'}
        </Button>
      </CardContent>
    </Card>
  );
}

const FINAL_STATUSES = ['approved', 'rejected', 'cancelled', 'expired'];

function useExpiryBanner(assessments: PortalAssessment[] | undefined): boolean {
  if (!assessments || assessments.length === 0) return false;
  const allFinal = assessments.every((a) => FINAL_STATUSES.includes(a.status));
  if (!allFinal) return false;
  // Find the most recent submitted_at or issued_at across all final assessments
  const dates = assessments
    .map((a) => a.submitted_at ?? null)
    .filter(Boolean) as string[];
  if (dates.length === 0) return false;
  const latest = dates.reduce((a, b) => (a > b ? a : b));
  return differenceInDays(new Date(), parseISO(latest)) > 30;
}

export function PortalDashboardPage() {
  const navigate = useNavigate();
  const { data: me } = usePortalMe();
  const { data: assessments, isLoading } = usePortalAssessments();
  const active = (assessments ?? []).filter((a) => !FINAL_STATUSES.includes(a.status));
  const showExpiryBanner = useExpiryBanner(assessments);

  const { data: certs, isLoading: certsLoading } = usePortalCertifications();
  const activeCerts = (certs ?? []).filter((c) => c.lifecycle_status === 'active');
  const urgentCerts = activeCerts
    .filter((c) => c.expiry_date !== null && differenceInDays(parseISO(c.expiry_date), new Date()) <= 30)
    .sort((a, b) => ((a.expiry_date ?? '') < (b.expiry_date ?? '') ? -1 : 1))
    .slice(0, 3);

  return (
    <div className="space-y-6">
      {/* Access expiry warning */}
      {showExpiryBanner && (
        <div className="flex items-start gap-3 rounded-lg border border-accent-amber/30 bg-accent-amber/10 px-4 py-3">
          <Info className="h-4 w-4 text-accent-amber mt-0.5 shrink-0" />
          <p className="text-sm text-text-primary">
            All of your assessments have been completed for more than 30 days. Your portal access may expire soon — contact your customer if you expect further assessments.
          </p>
        </div>
      )}

      {/* Welcome */}
      <div className="rounded-xl border border-border-subtle bg-bg-surface p-5">
        <h1 className="text-xl font-bold text-text-primary">{portalGreeting(me?.first_name)}</h1>
        <p className="mt-1 text-sm text-text-secondary">
          {active.length > 0
            ? `You have ${active.length} active assessment${active.length !== 1 ? 's' : ''} to complete`
            : 'You have no active assessments at this time'}
        </p>
      </div>

      {/* Active assessments */}
      <div>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-2">
          <ClipboardList className="h-4 w-4" />
          Active Assessments
        </h2>

        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {[...Array(2)].map((_, i) => <Skeleton key={i} className="h-52 w-full rounded-xl" />)}
          </div>
        ) : active.length === 0 ? (
          <Card>
            <CardContent className="py-12 flex flex-col items-center gap-3">
              <ClipboardList className="h-10 w-10 text-text-secondary/40" />
              <p className="text-sm text-text-secondary">No active assessments</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {active.map((a) => <AssessmentCard key={a.id} assessment={a} />)}
          </div>
        )}
      </div>

      {/* My Certifications */}
      <div>
        <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wider mb-3 flex items-center gap-2">
          <Award className="h-4 w-4" />
          My Certifications
        </h2>

        {certsLoading ? (
          <Skeleton className="h-32 w-full rounded-xl" />
        ) : !certs || certs.length === 0 ? (
          <Card>
            <CardContent className="py-8 flex flex-col items-center gap-3">
              <Award className="h-10 w-10 text-text-secondary/40" />
              <p className="text-sm text-text-secondary">No certifications yet</p>
              <Button size="sm" variant="ghost" onClick={() => navigate('/portal/certifications')}>
                Upload Certification
              </Button>
            </CardContent>
          </Card>
        ) : urgentCerts.length === 0 ? (
          <Card>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-accent-emerald">All certifications current</p>
                <Badge variant="muted" className="font-mono">{activeCerts.length} active</Badge>
              </div>
              <Button size="sm" variant="ghost" className="w-full" onClick={() => navigate('/portal/certifications')}>
                View All Certifications
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="muted" className="font-mono">{activeCerts.length} certifications</Badge>
                <Badge variant="amber">{urgentCerts.length} expiring soon</Badge>
              </div>
              <div className="space-y-2">
                {urgentCerts.map((cert) => {
                  const days = cert.expiry_date
                    ? differenceInDays(parseISO(cert.expiry_date), new Date())
                    : 0;
                  return (
                    <div key={cert.id} className="flex items-center justify-between gap-3">
                      <div className="min-w-0 space-y-0.5">
                        <p className="text-sm font-medium text-text-primary truncate">{cert.cert_type}</p>
                        {cert.issuing_body && (
                          <p className="text-xs text-text-secondary truncate">{cert.issuing_body}</p>
                        )}
                        <p className="text-xs text-text-secondary">
                          {days <= 0 ? 'Expired' : `Expires in ${days} days`}
                        </p>
                      </div>
                      <StatusBadge
                        status={days <= 0 ? 'expired' : 'expiring_soon'}
                        label={days <= 0 ? 'Expired' : `Expiring in ${days} days`}
                      />
                    </div>
                  );
                })}
              </div>
              <Button size="sm" variant="ghost" className="w-full" onClick={() => navigate('/portal/certifications')}>
                View All Certifications
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
